import { useTranslation } from 'react-i18next';
import EmptyState from '../../Fundamentals/EmptyState/EmptyState';
import DropdownMenu from '../../Fundamentals/DropdownMenu/DropdownMenu';
import SpirisIcon from '../../Fundamentals/Icon/SpirisIcon';
import Spinner from '../../Fundamentals/Spinner/Spinner';
import PackagePlanCard from './PackagePlanCard';
import type { PackagePlan } from './Packages.types';

interface PackagePlansTabProps {
    plans: PackagePlan[] | undefined;
    isLoading: boolean;
    canEdit: boolean;
    onAdd: () => void;
    onCopy: () => void;
    onEdit: (plan: PackagePlan) => void;
    onDelete: (plan: PackagePlan) => void;
}

const PackagePlansTab = ({ plans, isLoading, canEdit, onAdd, onCopy, onEdit, onDelete }: PackagePlansTabProps) => {
    const { t } = useTranslation();

    const list = () => {
        if (plans === undefined) return isLoading ? <Spinner /> : null;

        if (plans.length === 0)
            return (
                <EmptyState
                    icon="credit-card"
                    title={t('packages.noPlans') ?? ''}
                    description={(canEdit ? t('packages.noPlansDescription') : t('packages.noPlansReadOnly')) ?? ''}
                />
            );

        return (
            <div className="d-flex flex-column gap-16">
                {plans.map((plan) => (
                    <PackagePlanCard key={plan.id} plan={plan} canEdit={canEdit} onEdit={() => onEdit(plan)} onDelete={() => onDelete(plan)} />
                ))}
            </div>
        );
    };

    return (
        <>
            <div className="d-flex align-items-start justify-content-between gap-16 flex-wrap mb-16">
                <div>
                    <h3 className="heading-lg mb-4">{t('packages.plansTitle')}</h3>
                    <p className="text-secondary font-13 mb-0">{t('packages.plansDescription')}</p>
                </div>
                {canEdit && (
                    <DropdownMenu
                        label={t('packages.addPlan') ?? ''}
                        trigger={
                            <>
                                <SpirisIcon name="plus" size="sm" className="mr-8" />
                                {t('packages.addPlan')}
                            </>
                        }
                        items={[
                            { key: 'new', label: t('packages.newPlan') ?? '', icon: 'plus', onClick: onAdd },
                            { key: 'copy', label: t('packages.copyFromExisting') ?? '', icon: 'copy', onClick: onCopy },
                        ]}
                    />
                )}
            </div>
            {list()}
        </>
    );
};

export default PackagePlansTab;
